import { UserAccessBlock } from "modules/session/access-links/BlockedName";
import { accessLinkActions } from "modules/session/access-links/access-link-actions"
import { types, applySnapshot, isAlive, getSnapshot } from "mobx-state-tree"
import { BaseEntity, isNewlyCreated } from "modules/common/models/entity"
import { Transport } from "modules/common/models/transport"
import { Notificator } from "modules/common/models/notificator"
import { apiUrls } from "modules/common/services/communication/urls"
import { flow } from "modules/common/models/flow"
import { EMPTY_OBJECT_ID, DATE_TIME_FORMAT } from "modules/common/constants"
import Schema from "../components/details/validation"
import moment from "moment"
import { texts } from "modules/common/texts"
import { nameof } from "modules/common/services/typescript"
import { getFieldLabel } from "modules/common/services/form/fields"
import { formatDate } from "modules/common/services/formatting/date"
import { DictionaryLink, initialState as emptyLink } from "modules/common/models/dictionary-link"
import { groupBy } from "lodash"
import { MimeTypes, base64ToBlob, printPdf, treatBase64, arrayBufferToBase64 } from "modules/common/services/files"
import { FileMetadata } from "modules/common/models/file-metadata"
import { formatPhoneNumber } from "modules/common/services/formatting/phone"
import {
  EmployeePositionDictionaryLink,
  EmployeePositionLink,
} from "modules/spending/departments/models/employee-position"
import { RoleAccessRecord, RoleData } from "modules/session/access/models/access-store"
import { v4 } from "uuid"

export const ACCEPT = [MimeTypes.jpeg, MimeTypes.png, MimeTypes.pdf].join(",")

/** заглушки импорта из 1С (EmployerPosition.NoPositionName / Department.NoDepartmentName на сервере) */
export const NO_POSITION_NAME = "Без должности"
export const NO_DEPARTMENT_NAME = "Без отдела"

export const EmployerDocument = types
  .compose(
    FileMetadata,
    types.model({
      /** блок документов, которому принадлежит файл */
      blockGuid: types.optional(types.string, ""),
    })
  )
  .views((self) => ({
    get asFileBase(): EmployerFileBase {
      return {
        fileId: self.id,
        fileName: self.name,
        mimeType: self.mimeType,
      }
    },
  }))
  .named("EmployerDocument")

/** Блок документов: свободное примечание + привязанные к нему файлы */
export const EmployerDocumentBlock = types
  .model({
    guid: types.string,
    note: types.optional(types.string, ""),
  })
  .named("EmployerDocumentBlock")

const Child = types
  .model({
    guid: types.string,
    name: types.string,
    birthday: types.maybeNull(types.string),
  })
  .views((self) => ({
    get birthdayAsDate() {
      return self.birthday ? moment(self.birthday, DATE_TIME_FORMAT).toDate() : null
    },
  }))
  .named("Child")

export const GradeHistoryRecord = types.model({
  gradeGuid: types.maybeNull(types.string),
  gradeName: types.optional(types.string, ""),
  percent: types.optional(types.number, 0),
  since: types.optional(types.string, ""),
})

export const EmployerBase = types
  .compose(
    Transport,
    Notificator,
    BaseEntity,
    types.model({
      name: types.string,
      shortName: types.string,
      phone: types.string,
      avatar: types.maybeNull(types.string),
      email: types.string,
      poohBah: types.boolean,
      birthday: types.maybeNull(types.string),
      signDateTo: types.maybeNull(types.string),
      fireDate: types.maybeNull(types.string),
      /** Доступ к приложению отключён (настройки → Пользователи) */
      accessBlocked: types.optional(types.maybeNull(types.frozen<UserAccessBlock>()), null),
      fireDateSortable: types.string,
      hireDate: types.maybeNull(types.string),
      workSince: types.maybeNull(types.string),
      /** С какой даты сейчас считается стаж (дата приёма или сброса стажа при переводе) */
      seniorityDate: types.optional(types.maybeNull(types.string), null),
      /** Дата приёма в текущую организацию (перевод в неё) */
      organizationHireDate: types.optional(types.maybeNull(types.string), null),
      hobby: types.maybeNull(types.string),
      account: types.string,
      contractNumber: types.string,
      address: types.string,
      isBim: types.boolean,
      isRemoved: types.boolean,
      canBeEngineer: types.boolean,
      documents: types.array(EmployerDocument),
      documentBlocks: types.optional(types.array(EmployerDocumentBlock), []),
      employerPosition: EmployeePositionDictionaryLink,
      employerPositions: types.array(EmployeePositionLink),
      gradeHistory: types.optional(types.array(GradeHistoryRecord), []),
      department: DictionaryLink,
      login: types.string,
      newPassword: types.string,
      tgUser: types.string,
      tgRegistered: types.boolean,
      access: types.array(RoleAccessRecord),
      /** администратор (GodMode): все права, вход без подтверждения устройства */
      godMode: types.optional(types.boolean, false),
      workingPeriod: types.string,
      agePeriod: types.string,
      canBeReturn: types.boolean,
      children: types.array(Child),
      workQuestions: types.number,
      reviewQuestions: types.optional(types.number, 0),
      expQuestions: types.number,
      portfolioId: types.maybeNull(types.string),
      orgName: types.maybeNull(types.string),

    })
  )
  .views((self) => ({
    get childCount() {
      return self.children ? self.children.length.toString() : "0"
    },
    get birthdayAsDate() {
      return self.birthday ? moment(self.birthday, DATE_TIME_FORMAT).toDate() : null
    },
    get signToAsDate() {
      return self.signDateTo ? moment(self.signDateTo, DATE_TIME_FORMAT).toDate() : null
    },
    get hireDateAsDate() {
      return self.hireDate ? moment(self.hireDate, DATE_TIME_FORMAT).toDate() : null
    },
    get fireDateAsDate() {
      return self.fireDate ? moment(self.fireDate, DATE_TIME_FORMAT).toDate() : null
    },
    get workSinceAsDate() {
      return self.workSince ? moment(self.workSince, DATE_TIME_FORMAT).toDate() : null
    },
    get fired() {
      return fired(self)
    },
    get employePositionId() {
      return self.employerPosition.id
    },
    get departmentId() {
      return self.department.id
    },
    /** должность не указана (импорт из 1С - пусто или заглушка): выбирается в карточке, а не переводом */
    get positionUnset() {
      return !self.employerPosition.id || self.employerPosition.name === NO_POSITION_NAME
    },
    /** подразделение не указано (импорт из 1С - пусто или заглушка): выбирается в карточке, а не переводом */
    get departmentUnset() {
      return !self.department.id || self.department.name === NO_DEPARTMENT_NAME
    },
    /**
     * Блоки документов в виде значения для formik (обычные объекты, не узлы MST).
     * Файлы, чей блок неизвестен (данные до перехода на блоки), не теряем —
     * собираем их в блоки по прежнему типу документа
     */
    get documentBlocksForm(): EmployerDocumentBlockForm[] {
      const blocks = self.documentBlocks.map((block) => ({
        guid: block.guid,
        note: block.note,
        files: self.documents.filter((doc) => doc.blockGuid === block.guid).map((doc) => doc.asFileBase),
      }))

      const known = new Set(self.documentBlocks.map((block) => block.guid))
      const orphans = self.documents.filter((doc) => !known.has(doc.blockGuid))

      Object.entries(groupBy(orphans, (doc) => doc.type)).forEach(([note, files]) => {
        // guid именно детерминированный: значение поля формы пересчитывается на каждый
        // рендер, и со случайным guid formik переинициализировался бы без остановки
        blocks.push({ guid: `legacy-${note}`, note, files: files.map((doc) => doc.asFileBase) })
      })

      return blocks
    },
    get formattedPhone() {
      return formatPhoneNumber(self.phone)
    },

    get role(): RoleData {
      return {
        access: getSnapshot(self.access),
        id: self.id,
        label: self.name,
        sublabel: self.employerPosition.name,
      }
    },
  }))

export const EmployerLink = types.compose(
  DictionaryLink,
  types.model({
    position: types.string,
    login: types.string,
  })
)

export const Employer = EmployerBase.actions((self) => ({
    ...accessLinkActions(self as any, {
      target: "employer",
      status: (id: string) => apiUrls.accessLinks.employee(id),
      link: (id: string) => apiUrls.accessLinks.employeeLink(id),
    }),

  load: flow(function* (id: string) {
    try {
      const snapshot: any = isNewlyCreated(id)
        ? initialState()
        : yield self.transport.get<EmployerSnapshotType>(apiUrls.employee.details(id))

      applySnapshot(self, treat(snapshot))
      return true
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),

  loadProfile: flow(function* (id: string) {
    try {
      const snapshot: any = yield self.transport.get<EmployerSnapshotType>(apiUrls.employee.profile(id))
      applySnapshot(self, treat(snapshot))
      return true
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),

  /** Перевод на должность — сразу, отдельно от сохранения карточки */
  transfer: flow(function* (data: TransferData) {
    try {
      const snapshot: any = yield self.transport.post<any>(apiUrls.employee.transfer(self.id), data)
      applySnapshot(self, treat(snapshot))
      self.notify.success("Сотрудник переведён")
      return true
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),

  /** Отменить последний перевод (пока он не вступил в прошлые месяцы) */
  revertTransfer: flow(function* () {
    try {
      const snapshot: any = yield self.transport.delete<any>(apiUrls.employee.transfer(self.id))
      applySnapshot(self, treat(snapshot))
      self.notify.success("Перевод отменён")
      return true
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),

  /** Ранги должности (в справочнике должностей их нет) */
  loadPositionGrades: flow(function* (positionId: string) {
    try {
      const position: any = yield self.transport.get<any>(apiUrls.employeePositions.details(positionId))
      return (position?.grades ?? []) as { guid: string; gradeName: string; percent: number }[]
    } catch (er) {
      return []
    }
  }),

  /** История условий оплаты (нужно право на штатку) */
  loadConditions: flow(function* () {
    try {
      const rows: any[] = yield self.transport.get<any>(apiUrls.schedule.compensation(self.id))
      return rows ?? []
    } catch (er) {
      return []
    }
  }),

  getCredentials: flow(function* () {
    try {
      const result: EmployerCredentials = yield self.transport.post<any>(apiUrls.employee.credentials(self.id), {})
      return result
    } catch (er) {
      self.notify.error(er)
      return null
    }
  }),

  delete: flow(function* () {
    if (self.isNewlyCreated) {
      return false
    }

    try {
      yield self.transport.delete<boolean>(apiUrls.employee.delete(self.id))
      self.notify.success(texts.messages.removed)

      isAlive(self) && applySnapshot(self, initialState())
      return true
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),

  restore: flow(function* () {
    if (self.isNewlyCreated) {
      return false
    }

    try {
      const ok = yield self.transport.post<boolean>(apiUrls.employee.restore(self.id))
      if (isAlive(self)) {
        self.isRemoved = false
      }

      self.notify.success("Данные успешно восстановлены")
      return ok
    } catch (er) {
      self.notify.error(er)
      return false
    }
  }),
}))
  .actions((self) => ({
    getFile: flow(function* (field) {
      try {
        const data: any = yield self.transport.get<EmployerSnapshotType[]>(
          apiUrls.application.files.content(field.fileId, field.fileName),
          {
            responseType: "arraybuffer",
          }
        )

        const buff = arrayBufferToBase64(data)
        const fimageSrc = treatBase64(buff, "image/jpeg")
        return fimageSrc
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    save: flow(function* (patch: TStringMap<any>) {
      try {
        // convert input dates
        if (patch[fields.fireDate] instanceof Date) {
          patch[fields.fireDate] = formatDate(patch[fields.fireDate])
        }

        if (patch[fields.birthday] instanceof Date) {
          patch[fields.birthday] = formatDate(patch[fields.birthday])
        }

        if (patch[fields.signDateTo] instanceof Date) {
          patch[fields.signDateTo] = formatDate(patch[fields.signDateTo])
        }

        if (patch[fields.hireDate] instanceof Date) {
          patch[fields.hireDate] = formatDate(patch[fields.hireDate])
        }

        if (patch[fields.workSince] instanceof Date) {
          patch[fields.workSince] = formatDate(patch[fields.workSince])
        }

        if (patch.documentBlocks) {
          patch.documentBlocks = (patch.documentBlocks as EmployerDocumentBlockForm[]).map((block) => ({
            guid: block.guid,
            note: block.note,
            files: block.files.map((file) => file.fileId),
          }))
        }

        if (typeof patch[fields.salary] === "undefined") {
          patch[fields.salary] = 0
        } else if (typeof patch[fields.salary] === "string") {
          patch[fields.salary] = parseFloat(patch[fields.salary])
        }
        if (typeof patch[fields.bonus] === "undefined") {
          patch[fields.bonus] = 0
        } else if (typeof patch[fields.bonus] === "string") {
          patch[fields.bonus] = parseFloat(patch[fields.bonus])
        }
        if (patch.gradeGuid !== undefined) {
          let gradeGuidValue = patch.gradeGuid
          if (gradeGuidValue === "null") {
            gradeGuidValue = null
          }

          // Добавляем gradeGuid в patch для отправки на сервер
          patch.gradeGuid = gradeGuidValue

          delete patch[fields.changeGrade]
        }
        if (patch[fields.changePositionDate] instanceof Date) {
          patch[fields.changePositionDate] = formatDate(patch[fields.changePositionDate])
        }
        if (patch[fields.changeGradeSince] instanceof Date) {
          patch[fields.changeGradeSince] = formatDate(patch[fields.changeGradeSince])
        }
        if (patch[fields.changeHireDate] instanceof Date) {
          patch[fields.changeHireDate] = formatDate(patch[fields.changeHireDate])
        }
        if (typeof patch[fields.changeSalary] === "undefined") {
          patch[fields.changeSalary] = 0
        } else if (typeof patch[fields.changeSalary] === "string") {
          patch[fields.changeSalary] = parseFloat(patch[fields.changeSalary])
        }
        if (typeof patch[fields.changeBonus] === "undefined") {
          patch[fields.changeBonus] = 0
        } else if (typeof patch[fields.changeBonus] === "string") {
          patch[fields.changeBonus] = parseFloat(patch[fields.changeBonus])
        }

        patch[fields.children] = self.children

        const snapshot: any = self.isNewlyCreated
          ? yield self.transport.put<EmployerSnapshotType>(apiUrls.employee.create(), patch)
          : yield self.transport.post<EmployerSnapshotType>(apiUrls.employee.update(self.id), patch)

        isAlive(self) && applySnapshot(self, treat(snapshot))

        self.notify.success(texts.messages.saved)

        return true
      } catch (er) {
        self.notify.error(er)

        return false
      }
    }),
    saveProfile: flow(function* (patch: TStringMap<any>) {
      try {
        if (patch[fields.birthday] instanceof Date) {
          patch[fields.birthday] = formatDate(patch[fields.birthday])
        }

        if (patch[fields.signDateTo] instanceof Date) {
          patch[fields.signDateTo] = formatDate(patch[fields.signDateTo])
        }

        const snapshot: any = yield self.transport.post<EmployerSnapshotType>(
          apiUrls.employee.saveProfile(self.id),
          patch
        )

        isAlive(self) && applySnapshot(self, treat(snapshot))

        self.notify.success(texts.messages.saved)

        return true
      } catch (er) {
        self.notify.error(er)

        return false
      }
    }),
    addChild(guid: string, name: string, birthday: string) {
      const child: ChildSnapshotType = { guid: v4(), name, birthday }
      const itemIndex = self.children.findIndex((ch) => ch.guid === guid)
      if (itemIndex > -1) {
        self.children[itemIndex].birthday = child.birthday
        self.children[itemIndex].name = child.name
      } else {
        self.children.push(child)
      }
    },
    removeChild(id: string) {
      const index = self.children.findIndex((c) => c.guid === id)
      if (index > -1) {
        self.children.splice(index, 1)
      }
    },
    uploadFile: flow(function* (file: File) {
      try {
        const model = new FormData()

        model.append("file", file)
        model.append("accept", ACCEPT)

        const result: UploadFileResult = yield self.transport.post<any>(apiUrls.application.files.upload, model)

        const { id } = result

        const uploaded: EmployerFileBase = { fileId: id, fileName: file.name }

        return uploaded
      } catch (er) {
        self.notify.error(er)
        return null
      }
    }),

    printDocuments: flow(function* (guids: string[]) {
      if (self.isNewlyCreated) {
        return false
      }

      try {
        const result: DownloadFileResult = yield self.transport.post<any>(
          apiUrls.employee.printUserDocuments(self.id),
          { guids }
        )

        const blob: any = yield base64ToBlob(result.content || "", result.mimeType)

        if (blob) {
          const fileURL = URL.createObjectURL(blob)
          const printer = printPdf(fileURL, true)
          if (printer) {
            printer.onclose = () => URL.revokeObjectURL(fileURL)
          }
        }
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    setGodMode: flow(function* (value: boolean) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employee.godMode(self.id), { value })

        self.godMode = data.godMode
        applySnapshot(self.access, data.access)
        self.notify.success(data.godMode ? "Сотрудник назначен администратором" : "Роль администратора снята")

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),

    setFunctionality: flow(function* (functionality: string, state: boolean) {
      try {
        const data: any = yield self.transport.post<any>(apiUrls.employee.singleAccess(self.id), {
          functionality,
          state,
        })

        applySnapshot(self.access, data)

        return true
      } catch (er) {
        self.notify.error(er)
        return false
      }
    }),
  }))
  .named("Employer")

/** mimeType знаем только у уже сохранённых файлов, у только что загруженного его нет */
export type EmployerFileBase = Omit<FileBase, "previewMimeType" | "mimeType"> & { mimeType?: string }

/** Блок документов как значение поля формы */
export interface EmployerDocumentBlockForm {
  guid: string
  note: string
  files: EmployerFileBase[]
}

export const treat = (snapshot: EmployerSnapshotType) => {
  snapshot.newPassword = ""
  return snapshot
}

export type EmployerLinkType = typeof EmployerLink.Type
export type EmployerSnapshotType = typeof EmployerBase.SnapshotType
export type EmployerType = typeof Employer.Type

export type ChildSnapshotType = typeof Child.SnapshotType
export type ChildType = typeof Child.Type

export const fired = (e: EmployerSnapshotType) => e.fireDate !== null

export const initialState = (): EmployerSnapshotType => ({
  id: EMPTY_OBJECT_ID,
  created: moment().format(DATE_TIME_FORMAT),
  name: "",
  shortName: "",
  phone: "",
  email: "",
  tgUser: "",
  tgRegistered: false,
  birthday: null,
  signDateTo: null,
  avatar: null,
  hireDate: null,
  fireDate: null,
  accessBlocked: null,
  hobby: null,
  fireDateSortable: "",
  poohBah: false,
  account: "",
  address: "",
  isBim: false,
  isRemoved: false,
  canBeEngineer: false,
  canBeReturn: false,
  contractNumber: "",
  login: "",
  newPassword: "",
  workSince: null,
  seniorityDate: null,
  organizationHireDate: null,
  documents: [],
  documentBlocks: [],
  employerPosition: { ...emptyLink(), nonProduction: false, order: null },
  department: emptyLink(),
  access: [],
  godMode: false,
  workingPeriod: "",
  agePeriod: "",
  children: [],
  employerPositions: [],
  gradeHistory: [],
  workQuestions: 0,
  reviewQuestions: 0,
  expQuestions: 0,
  portfolioId: null,
  orgName: null
})

export const initialChild = (): ChildSnapshotType => ({
  guid: "",
  name: "",
  birthday: null,
})

export const fields = {
  id: nameof((a: EmployerType) => a.id) as string,
  name: nameof((a: EmployerType) => a.name) as string,
  avatar: nameof((a: EmployerType) => a.avatar) as string,
  phone: nameof((a: EmployerType) => a.phone) as string,
  email: nameof((a: EmployerType) => a.email) as string,
  birthday: nameof((a: EmployerType) => a.birthday) as string,
  signDateTo: nameof((a: EmployerType) => a.signDateTo) as string,
  poohBah: nameof((a: EmployerType) => a.poohBah) as string,
  position: nameof((a: EmployerType) => a.employerPosition) as string,
  fireDate: nameof((a: EmployerType) => a.fireDate) as string,
  hireDate: nameof((a: EmployerType) => a.hireDate) as string,
  hobby: nameof((a: EmployerType) => a.hobby) as string,
  account: nameof((a: EmployerType) => a.account) as string,
  address: nameof((a: EmployerType) => a.address) as string,
  contractNumber: nameof((a: EmployerType) => a.contractNumber) as string,
  workSince: nameof((a: EmployerType) => a.workSince) as string,
  fired: nameof((a: EmployerType) => a.fired) as string,
  canBeReturn: nameof((a: EmployerType) => a.canBeReturn) as string,
  documents: nameof((a: EmployerType) => a.documents) as string,
  documentBlocks: nameof((a: EmployerType) => a.documentBlocks) as string,
  positionId: nameof((a: EmployerType) => a.employePositionId) as string,
  changePositionId: "changePositionId",
  /** подразделение из дерева структуры (по умолчанию — из должности) */
  departmentId: "departmentId",
  changeHireDate: "changeHireDate",
  changePositionDate: "changePositionDate",
  changeSalary: "changeSalary",
  changeBonus: "changeBonus",
  changeGrade: "changeGrade",
  changeGradeSince: "changeGradeSince",
  gradeGuid: "gradeGuid",
  changeEmployerId: "changeEmployerId",
  isBim: nameof((a: EmployerType) => a.isBim) as string,
  isRemoved: nameof((a: EmployerType) => a.isRemoved) as string,
  canBeEngineer: nameof((a: EmployerType) => a.canBeEngineer) as string,
  login: nameof((a: EmployerType) => a.login) as string,
  newPassword: nameof((a: EmployerType) => a.newPassword) as string,
  tgUser: nameof((a: EmployerType) => a.tgUser) as string,
  salary: "salary",
  bonus: "bonus",
  children: nameof((a: EmployerType) => a.children) as string,
  workQuestions: nameof((a: EmployerType) => a.workQuestions) as string,
  portfolioId: nameof((a: EmployerType) => a.portfolioId) as string,
}

export const childFields = {
  name: nameof((a: ChildType) => a.name) as string,
  birthday: nameof((a: ChildType) => a.birthday) as string,
}

export function formatEmployer(employer: EmployerSnapshotType | null) {
  let result = ""

  if (employer) {
    const schema = Schema()

    result += `${getFieldLabel(fields.name, schema, null)}: ${employer.name}\n`
    result += `${getFieldLabel(fields.positionId, schema, null)}: ${employer.employerPosition.name}\n`
    result += `${getFieldLabel(fields.phone, schema, null)}: ${formatPhoneNumber(employer.phone)}\n`
    result += `${getFieldLabel(fields.address, schema, null)}: ${employer.address}\n`
    result += `${getFieldLabel(fields.email, schema, null)}: ${employer.email}\n`
    result += `${getFieldLabel(fields.poohBah, schema, null)}: ${employer.poohBah ? "Да" : "Нет"}\n`
    result += `${getFieldLabel(fields.isBim, schema, null)}: ${employer.isBim ? "Да" : "Нет"}\n`
    result += `${getFieldLabel(fields.birthday, schema, null)}: ${employer.birthday || ""}\n`
    result += `${getFieldLabel(fields.workSince, schema, null)}: ${employer.workSince || ""}\n`
    result += `${getFieldLabel(fields.fired, schema, null)}: ${employer.fireDate || ""}\n`
  }

  return result
}

export interface EmployerCredentials {
  login: string
  password: string
}

export const EmployeeFunctionalities = {
  INTERNAL_USER_ACCESS: "INTERNAL_USER_ACCESS",
}

export interface TransferData {
  positionId: string
  date: string
  departmentId: string | null
  portfolioId: string | null
  resetSeniority: boolean
  gradeGuid: string | null
  salary: number | null
  bonus: number | null
}
